'use client';

// Shown at the top of /profile when the signed-in user is suspended. Suspend
// does NOT block login (Jason 2026-07-13): the user signs in, sees why here,
// fixes the flagged content (bio = "More Info / Bio" in Edit Profile), and is
// reviewed for reinstatement. Fetches the current user's own moderation status.
import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ShieldAlert } from 'lucide-react';
import { apiFetch } from '@/lib/api';

type ModStatus = { suspended: boolean; reason?: string | null; suspendedAt?: string | null };

export default function SuspendedBanner() {
  const [status, setStatus] = useState<ModStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 2-arg apiFetch; no-store so the banner reflects a just-lifted suspension.
        const res = await apiFetch('/api/proxy/api/profile/moderation-status', { method: 'GET', cache: 'no-store' });
        if (!res.ok) return;
        const json = (await res.json()) as ModStatus;
        if (!cancelled) setStatus(json);
      } catch {
        /* silent — never block the profile page on this */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!status?.suspended) return null;

  return (
    <div
      role="alert"
      className="mb-4 rounded-lg border border-orange-300 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-950/40"
    >
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-600" aria-hidden />
        <div className="flex-1 text-sm">
          <p className="font-semibold text-orange-800 dark:text-orange-200">Your account is suspended</p>
          <p className="mt-1 text-orange-700 dark:text-orange-300">
            Your public profile and content are hidden from JuicyStarz while this is resolved. You can still edit your
            profile to fix the issue; your account is then reviewed for reinstatement.
          </p>
          {status.reason && (
            <p className="mt-2 rounded border-l-4 border-orange-400 bg-white/60 px-3 py-2 text-orange-900 dark:bg-black/20 dark:text-orange-100">
              <span className="font-medium">Reason:</span> {status.reason}
            </p>
          )}
          <Link
            href="/profile/editprofile"
            className="mt-3 inline-block rounded-md bg-orange-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-700"
          >
            Edit my profile
          </Link>
        </div>
      </div>
    </div>
  );
}
